<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Draggable from 'vuedraggable';
import ProjectsAPI from 'dashboard/api/projects';
import tasksApi from 'dashboard/api/tasks';

import Button from 'dashboard/components-next/button/Button.vue';
import TaskBoard from './TaskBoard.vue';

const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true,
  },
  phases: {
    type: Array,
    default: () => [],
  },
  // Optional phase to pre-select when entering the board (e.g. from Phases tab).
  initialPhaseId: {
    type: [String, Number],
    default: null,
  },
});

const router = useRouter();
const { t } = useI18n();

const STATUS_CATEGORIES = ['unstarted', 'started', 'done', 'canceled'];

const GATE_CLASS = {
  pending: 'bg-n-amber-3 text-n-amber-11',
  approved: 'bg-n-teal-3 text-n-teal-11',
  rejected: 'bg-n-ruby-3 text-n-ruby-11',
};

// Local, position-ordered copy of phases for the draggable rail.
const railPhases = ref([]);

const selectedPhaseId = ref(null);

const board = ref({ stages: [], columns: {}, unassigned: [] });
const isLoadingBoard = ref(false);

// Stage management toolbar state.
const isAddingStage = ref(false);
const newStageName = ref('');
const newStageCategory = ref('unstarted');
const isSavingStage = ref(false);

// Inline stage editing.
const editingStageId = ref(null);
const editStageName = ref('');
const editStageCategory = ref('unstarted');

const orderedPhases = computed(() =>
  [...props.phases].sort((a, b) => (a.position || 0) - (b.position || 0))
);

const selectedPhase = computed(
  () => railPhases.value.find(p => p.id === selectedPhaseId.value) || null
);

const stages = computed(() => board.value.stages || []);
const sortedStages = computed(() =>
  [...stages.value].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
);

const phaseTaskCount = phase => {
  const list = phase.pipeline_stages || [];
  if (list.length) {
    return list.reduce((sum, s) => sum + (s.tasks_count || 0), 0);
  }
  return phase.tasks_count || 0;
};

const syncRailPhases = () => {
  railPhases.value = orderedPhases.value.map(p => ({ ...p }));
};

const loadBoard = async () => {
  if (!selectedPhaseId.value) {
    board.value = { stages: [], columns: {}, unassigned: [] };
    return;
  }
  isLoadingBoard.value = true;
  try {
    const { data } = await ProjectsAPI.phaseBoard(
      props.projectId,
      selectedPhaseId.value
    );
    board.value = {
      stages: data.stages || [],
      columns: data.columns || {},
      unassigned: data.unassigned || [],
    };
  } catch (error) {
    useAlert(t('PROJECTS.BOARD.LOAD_ERROR'));
  } finally {
    isLoadingBoard.value = false;
  }
};

const selectPhase = phaseId => {
  if (selectedPhaseId.value === phaseId) return;
  selectedPhaseId.value = phaseId;
  editingStageId.value = null;
  isAddingStage.value = false;
  loadBoard();
};

// Keep the rail in sync when the parent reloads project phases.
watch(
  () => props.phases,
  () => {
    syncRailPhases();
    if (
      !selectedPhaseId.value ||
      !railPhases.value.some(p => p.id === selectedPhaseId.value)
    ) {
      selectedPhaseId.value = railPhases.value[0]?.id ?? null;
      loadBoard();
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => props.initialPhaseId,
  phaseId => {
    if (phaseId && railPhases.value.some(p => p.id === Number(phaseId))) {
      selectPhase(Number(phaseId));
    }
  }
);

// --- Phase reorder ---------------------------------------------------------
const onPhaseReorder = async () => {
  const phaseIds = railPhases.value.map(p => p.id);
  try {
    await ProjectsAPI.reorderPhases(props.projectId, phaseIds);
  } catch (error) {
    useAlert(t('PROJECTS.BOARD.REORDER_ERROR'));
    syncRailPhases();
  }
};

// --- Stage management ------------------------------------------------------
const startAddStage = () => {
  isAddingStage.value = true;
  newStageName.value = '';
  newStageCategory.value = 'unstarted';
};

const cancelAddStage = () => {
  isAddingStage.value = false;
  newStageName.value = '';
};

const submitAddStage = async () => {
  const name = newStageName.value.trim();
  if (!name || !selectedPhaseId.value || isSavingStage.value) return;
  isSavingStage.value = true;
  try {
    await ProjectsAPI.createPipelineStage(
      props.projectId,
      selectedPhaseId.value,
      {
        name,
        status_category: newStageCategory.value,
      }
    );
    cancelAddStage();
    await loadBoard();
  } catch (error) {
    useAlert(t('PROJECTS.STAGES.SAVE_ERROR'));
  } finally {
    isSavingStage.value = false;
  }
};

const startEditStage = stage => {
  editingStageId.value = stage.id;
  editStageName.value = stage.name;
  editStageCategory.value = stage.status_category || 'unstarted';
};

const cancelEditStage = () => {
  editingStageId.value = null;
  editStageName.value = '';
};

const submitEditStage = async stage => {
  const name = editStageName.value.trim();
  if (!name || isSavingStage.value) return;
  isSavingStage.value = true;
  try {
    await ProjectsAPI.updatePipelineStage(
      props.projectId,
      selectedPhaseId.value,
      stage.id,
      { name, status_category: editStageCategory.value }
    );
    cancelEditStage();
    await loadBoard();
  } catch (error) {
    useAlert(t('PROJECTS.STAGES.SAVE_ERROR'));
  } finally {
    isSavingStage.value = false;
  }
};

const deleteStage = async stage => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PROJECTS.STAGES.DELETE_CONFIRM'))) return;
  try {
    await ProjectsAPI.deletePipelineStage(
      props.projectId,
      selectedPhaseId.value,
      stage.id
    );
    await loadBoard();
  } catch (error) {
    useAlert(t('PROJECTS.STAGES.DELETE_ERROR'));
  }
};

const moveStage = async (stage, direction) => {
  const ids = sortedStages.value.map(s => s.id);
  const idx = ids.indexOf(stage.id);
  const target = idx + direction;
  if (target < 0 || target >= ids.length) return;
  [ids[idx], ids[target]] = [ids[target], ids[idx]];
  try {
    await ProjectsAPI.reorderPipelineStages(
      props.projectId,
      selectedPhaseId.value,
      ids
    );
    await loadBoard();
  } catch (error) {
    useAlert(t('PROJECTS.STAGES.REORDER_ERROR'));
  }
};

// --- Task interactions -----------------------------------------------------
const onAddTask = async ({ stageId, title }) => {
  try {
    await tasksApi.create({
      title,
      project_phase_id: selectedPhaseId.value,
      pipeline_stage_id: stageId,
    });
    await loadBoard();
  } catch (error) {
    useAlert(t('PROJECTS.BOARD.ADD_TASK_ERROR'));
  }
};

const onMoveTask = async ({ taskId, toStageId }) => {
  try {
    await tasksApi.update(taskId, { pipeline_stage_id: toStageId });
    await loadBoard();
  } catch (error) {
    useAlert(t('PROJECTS.BOARD.MOVE_TASK_ERROR'));
    await loadBoard();
  }
};

const onOpenTask = task => {
  const taskId = task.display_id ?? task.id;
  if (!taskId) return;
  router.push({ name: 'task_details', params: { taskId } });
};

onMounted(() => {
  if (props.initialPhaseId) {
    nextTick(() => {
      if (railPhases.value.some(p => p.id === Number(props.initialPhaseId))) {
        selectPhase(Number(props.initialPhaseId));
      }
    });
  }
});
</script>

<template>
  <div class="flex flex-col min-h-0 -mx-4 -my-4 sm:-mx-5 sm:-my-5">
    <!-- Empty: no phases -->
    <div
      v-if="!railPhases.length"
      class="flex flex-col items-center justify-center gap-2 py-12 text-center"
    >
      <span
        class="flex items-center justify-center rounded-2xl size-12 bg-n-alpha-1 text-n-slate-10"
      >
        <span class="i-lucide-kanban size-6" />
      </span>
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('PROJECTS.BOARD.NO_PHASES_TITLE') }}
      </p>
      <p class="max-w-xs text-sm text-n-slate-10">
        {{ t('PROJECTS.BOARD.NO_PHASES_HINT') }}
      </p>
    </div>

    <template v-else>
      <!-- Phase rail -->
      <div
        class="flex items-center gap-2 px-4 py-3 overflow-x-auto border-b sm:px-5 border-n-weak shrink-0"
      >
        <Draggable
          v-model="railPhases"
          item-key="id"
          :animation="150"
          ghost-class="opacity-40"
          class="flex items-center gap-2"
          @end="onPhaseReorder"
        >
          <template #item="{ element: phase }">
            <button
              type="button"
              class="flex items-center gap-2 px-3 h-9 text-sm rounded-lg border transition-colors shrink-0 cursor-grab active:cursor-grabbing"
              :class="
                selectedPhaseId === phase.id
                  ? 'border-n-brand bg-n-brand/10 text-n-slate-12 font-medium'
                  : 'border-n-weak text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1'
              "
              @click="selectPhase(phase.id)"
            >
              <span class="truncate max-w-[12rem]">{{ phase.name }}</span>
              <span
                v-if="phase.is_gate"
                class="inline-flex items-center px-1.5 py-0.5 text-xs rounded-md shrink-0"
                :class="
                  GATE_CLASS[phase.gate_status || 'pending'] ||
                  GATE_CLASS.pending
                "
              >
                {{
                  t(
                    `PROJECTS.PHASES.GATE_STATUS.${(
                      phase.gate_status || 'pending'
                    ).toUpperCase()}`
                  )
                }}
              </span>
              <span
                class="inline-flex items-center justify-center min-w-5 px-1 h-5 text-xs rounded-md tabular-nums shrink-0 bg-n-alpha-1 text-n-slate-11"
              >
                {{ phaseTaskCount(phase) }}
              </span>
            </button>
          </template>
        </Draggable>
      </div>

      <!-- Stage management toolbar -->
      <div
        v-if="selectedPhase"
        class="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b sm:px-5 border-n-weak shrink-0"
      >
        <span class="text-xs font-medium text-n-slate-10">
          {{ t('PROJECTS.STAGES.TITLE') }}
        </span>

        <!-- Existing stages with edit / delete / reorder -->
        <template v-for="(stage, idx) in sortedStages" :key="stage.id">
          <div
            v-if="editingStageId === stage.id"
            class="flex items-center gap-1.5 p-1 rounded-lg bg-n-alpha-1"
          >
            <input
              v-model="editStageName"
              type="text"
              class="h-7 w-28 px-2 text-sm border rounded-md reset-base bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
              @keyup.enter="submitEditStage(stage)"
              @keyup.esc="cancelEditStage"
            />
            <select
              v-model="editStageCategory"
              class="h-7 px-1.5 text-xs border rounded-md reset-base bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
            >
              <option v-for="c in STATUS_CATEGORIES" :key="c" :value="c">
                {{ t(`PROJECTS.STAGES.CATEGORY.${c.toUpperCase()}`) }}
              </option>
            </select>
            <Button
              size="sm"
              color="blue"
              icon="i-lucide-check"
              :is-loading="isSavingStage"
              @click="submitEditStage(stage)"
            />
            <Button
              size="sm"
              variant="ghost"
              color="slate"
              icon="i-lucide-x"
              @click="cancelEditStage"
            />
          </div>
          <div
            v-else
            class="flex items-center gap-0.5 pl-2.5 pr-1 h-8 rounded-lg group bg-n-alpha-1"
          >
            <span class="text-sm text-n-slate-12">{{ stage.name }}</span>
            <Button
              v-tooltip.top="t('PROJECTS.STAGES.MOVE_LEFT')"
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-chevron-left"
              :disabled="idx === 0"
              @click="moveStage(stage, -1)"
            />
            <Button
              v-tooltip.top="t('PROJECTS.STAGES.MOVE_RIGHT')"
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-chevron-right"
              :disabled="idx === sortedStages.length - 1"
              @click="moveStage(stage, 1)"
            />
            <Button
              v-tooltip.top="t('PROJECTS.STAGES.EDIT')"
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-pencil"
              @click="startEditStage(stage)"
            />
            <Button
              v-tooltip.top="t('PROJECTS.STAGES.DELETE')"
              variant="ghost"
              color="ruby"
              size="xs"
              icon="i-lucide-trash-2"
              @click="deleteStage(stage)"
            />
          </div>
        </template>

        <!-- Add stage -->
        <div
          v-if="isAddingStage"
          class="flex items-center gap-1.5 p-1 rounded-lg bg-n-alpha-1"
        >
          <input
            v-model="newStageName"
            type="text"
            :placeholder="t('PROJECTS.STAGES.NAME_PLACEHOLDER')"
            class="h-7 w-28 px-2 text-sm border rounded-md reset-base bg-n-solid-1 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
            @keyup.enter="submitAddStage"
            @keyup.esc="cancelAddStage"
          />
          <select
            v-model="newStageCategory"
            class="h-7 px-1.5 text-xs border rounded-md reset-base bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          >
            <option v-for="c in STATUS_CATEGORIES" :key="c" :value="c">
              {{ t(`PROJECTS.STAGES.CATEGORY.${c.toUpperCase()}`) }}
            </option>
          </select>
          <Button
            size="sm"
            color="blue"
            :label="t('PROJECTS.STAGES.ADD')"
            :is-loading="isSavingStage"
            :disabled="!newStageName.trim()"
            @click="submitAddStage"
          />
          <Button
            size="sm"
            variant="ghost"
            color="slate"
            icon="i-lucide-x"
            @click="cancelAddStage"
          />
        </div>
        <Button
          v-else
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-plus"
          :label="t('PROJECTS.STAGES.ADD')"
          @click="startAddStage"
        />
      </div>

      <!-- Board -->
      <div class="flex flex-col flex-1 min-h-[24rem]">
        <p
          v-if="!isLoadingBoard && !stages.length"
          class="flex-1 p-12 text-sm italic text-center text-n-slate-10"
        >
          {{ t('PROJECTS.STAGES.EMPTY') }}
        </p>
        <TaskBoard
          v-else
          :stages="board.stages"
          :columns="board.columns"
          :unassigned="board.unassigned"
          :loading="isLoadingBoard"
          @add="onAddTask"
          @move="onMoveTask"
          @open="onOpenTask"
        />
      </div>
    </template>
  </div>
</template>
